import { useSelectedProductId } from "hooks/useSelectedProductId";
import { observer } from "mobx-react";
// teacher env specific navigation menu
import type React from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { buildUrlWithPathParams } from "_helpers/utils/urlBuilder";
import profileIcon from "assets/images/icon-in-circle.svg";
import { NavigationMenu } from "components/NavigationMenuV2/NavigationMenu";
import { ProductType } from "constants/hst-constants";
import {
  GENERIC_ROUTES,
  TEACHER_HOME_PAGE_ROUTES,
  TEACHER_INSIGHT_PAGE_ROUTES,
  TEACHER_SCHEDULED_TEST_PAGE_ROUTES,
} from "constants/routes";
import { useDomainHandler } from "hooks/useDomainHandler";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useModuleAvailability } from "hooks/useModuleAvailability";
import { useProduct } from "hooks/useProduct";
import { useLanguage } from "hooks/useSelectedLanguage";
import type { User, UserUpdate } from "models/user/User";
import { Link, matchPath, useLocation } from "react-router-dom";
import { CssFlex } from "styles/helpers/layout";

import { AccessibilityMenuModal } from "components/AccessibilityMenuModal/AccessibilityMenuModal";
import {
  StyledAccessibilityMenuButton,
  StyledButton,
  StyledDropdownIcon,
  StyledIcon,
  StyledLogo,
  StyledLogoContainer,
  StyledNavigationBottomContainer,
  StyledNavigationContainer,
  StyledNavigationItemName,
  StyledOrphanNavigationItemName,
  StyledProfileButton,
} from "components/NavigationMenuV2/NavigationMenu.styled";
import { SideMenuModal } from "components/SideMenuModal";
import { modalIds } from "constants/modals-constants";
import { useModal } from "hooks/useModal";
import { ParagraphLarge, ParagraphMedium } from "styles/elements/Texts";
import { InsightProductSelector } from "../InsightProductSelector/InsightProductSelector";
import { StyledProductSelectorItem } from "./TeacherNavigationMenu.styled";

interface ComponentProps {
  className?: string;
  userDetails?: User;
  toggleSideMenu: () => void;
  shouldShowSideNav: boolean;
  onLogout: () => void;
  updateUserDetails: (userDetails: UserUpdate) => void;
  navigateToStudentEnvironment: () => void;
  dataCy?: string;
}

interface InsightPagesPathParams {
  productId: string;
}

export const TeacherNavigationMenu: React.FC<ComponentProps> = observer(
  ({
    className,
    userDetails,
    toggleSideMenu,
    shouldShowSideNav,
    onLogout,
    updateUserDetails,
    navigateToStudentEnvironment,
    dataCy,
  }) => {
    const {
      showTeacherNavigationDashboardOverview,
      showTeacherPracticeInsightsPage,
      showTeacherDiagnosticTestsPage,
      showTeacherInstructions,
      showImmersiveReader,
    } = useFeatureFlags();
    const { t } = useTranslation("homepage");
    const { currentLanguage } = useLanguage();
    const { isBusinessDomain, getCustomLogoByDomain, getStudentTermByDomain } = useDomainHandler();
    const { toggleModal, isModalOpen } = useModal();
    const location = useLocation();

    const { productsList, productDetails } = useProduct();
    const isTeacherAndCanInviteStudents = useMemo(
      () => userDetails?.allowedInviteProducts as boolean,
      [userDetails?.allowedInviteProducts],
    );
    const isTeacherABusinessUser = productsList?.every(
      (product) => product.type === ProductType.BUSINESS,
    );
    const productId = useSelectedProductId();

    const { isAdaptiveModuleAvailable, isWritingModuleAvailable } = useModuleAvailability();

    const isInsightsExpanded = location.pathname.includes("/insights");
    const isTestingExpanded = location.pathname.includes("/scheduled-test");

    const buildURLWithProductId = (path: string) => {
      return buildUrlWithPathParams(path, { productId: productId || "" });
    };

    const menuItems = useMemo(
      () => [
        ...(showTeacherNavigationDashboardOverview
          ? [
              {
                id: "teacher-dashboard",
                dataCy: "teacher-dashboard",
                nestingLevel: 0,
                title: (
                  <StyledOrphanNavigationItemName>
                    <StyledIcon iconName="far fa-file-contract" iconSize="lg" />
                    {t("teacherSideMenu.overview.title", "Overview")}
                  </StyledOrphanNavigationItemName>
                ),
                redirectPath: buildURLWithProductId(TEACHER_HOME_PAGE_ROUTES.DASHBOARD),
              },
            ]
          : []),
        ...(isTeacherAndCanInviteStudents
          ? [
              {
                id: "teacher-student-license",
                dataCy: "teacher-student-license",
                nestingLevel: 0,
                title: (
                  <StyledOrphanNavigationItemName>
                    <StyledIcon iconName="fas fa-user-plus" iconSize="lg" />
                    {t("teacherSideMenu.studentLicense.title", {
                      studentTerm: getStudentTermByDomain(),
                    })}
                  </StyledOrphanNavigationItemName>
                ),
                redirectPath: TEACHER_HOME_PAGE_ROUTES.STUDENT_LICENSE,
              },
            ]
          : []),
        {
          id: "teacher-groups",
          dataCy: "teacher-groups",
          expandedByDefault: false,
          nestingLevel: 0,
          title: (
            <StyledOrphanNavigationItemName>
              <StyledIcon iconName="fas fa-user-friends" iconSize="lg" />
              {t("teacherSideMenu.groups.title", "Groups")}
            </StyledOrphanNavigationItemName>
          ),
          redirectPath: TEACHER_HOME_PAGE_ROUTES.GROUPS,
        },
        ...(showTeacherInstructions && productDetails?.hasTeacherInstructions
          ? [
              {
                id: "teacher-instructions",
                dataCy: "teacher-instructions",
                nestingLevel: 0,
                title: (
                  <StyledOrphanNavigationItemName>
                    <StyledIcon iconName="far fa-file-alt" iconSize="lg" />
                    {t("teacherSideMenu.teacherInstructions.title", "Teacher instructions")}
                  </StyledOrphanNavigationItemName>
                ),
                redirectPath: buildURLWithProductId(TEACHER_HOME_PAGE_ROUTES.TEACHER_INSTRUCTIONS),
              },
            ]
          : []),
        {
          id: "teacher-insights",
          dataCy: "teacher-insights",
          expandedByDefault: isInsightsExpanded,
          title: (
            <StyledNavigationItemName>
              <StyledIcon iconName="far fa-chart-line" iconSize="lg" />
              {t("teacherSideMenu.insights.title", {
                studentTerm: getStudentTermByDomain({ usePlural: true }),
              })}
              <StyledDropdownIcon iconName="fas fa-angle-down" iconSize="lg" />
            </StyledNavigationItemName>
          ),
          subItems: [
            ...(isAdaptiveModuleAvailable
              ? [
                  {
                    id: "teacher-progress-insights",
                    dataCy: "teacher-progress-insights",
                    title: t("teacherSideMenu.insights.progress.title", "Basic skills"),
                    redirectPath: buildURLWithProductId(
                      TEACHER_INSIGHT_PAGE_ROUTES.PROGRESS_OVERVIEW,
                    ),
                  },
                ]
              : []),
            ...(isWritingModuleAvailable
              ? [
                  {
                    id: "teacher-writing-insights",
                    dataCy: "teacher-writing-insights",
                    title: t("teacherSideMenu.insights.writing.title", "Writing skills"),
                    redirectPath: buildURLWithProductId(TEACHER_INSIGHT_PAGE_ROUTES.WRITING_SKILLS),
                  },
                ]
              : []),
            ...(showTeacherPracticeInsightsPage
              ? [
                  {
                    id: "teacher-activity-insights",
                    dataCy: "teacher-activity-insights",
                    title: t("teacherSideMenu.insights.activity.title", "Practice insights"),
                    redirectPath: buildURLWithProductId(TEACHER_INSIGHT_PAGE_ROUTES.ACTIVITY),
                  },
                ]
              : []),
            ...(showTeacherDiagnosticTestsPage && !isTeacherABusinessUser
              ? [
                  {
                    id: "teacher-diagnostic-test-insights",
                    dataCy: "teacher-diagnostic-test-insights",
                    title: t("teacherSideMenu.insights.diagnosticTest.title", "Practice tests"),
                    redirectPath: buildURLWithProductId(
                      TEACHER_INSIGHT_PAGE_ROUTES.DIAGNOSTIC_TEST,
                    ),
                  },
                ]
              : []),
          ],
        },
        ...(!isTeacherABusinessUser
          ? [
              {
                id: "teacher-testing",
                dataCy: "teacher-testing",
                expandedByDefault: isTestingExpanded,
                title: (
                  <StyledNavigationItemName>
                    <StyledIcon iconName="far fa-flag" iconSize="lg" />
                    {t("teacherSideMenu.testing.title", "Testing")}
                    <StyledDropdownIcon iconName="fas fa-angle-down" iconSize="lg" />
                  </StyledNavigationItemName>
                ),
                subItems: [
                  {
                    id: "teacher-create-test",
                    dataCy: "teacher-create-test",
                    title: t("teacherSideMenu.testing.createTest.title", "Create a test"),
                    redirectPath: TEACHER_SCHEDULED_TEST_PAGE_ROUTES.SCHEDULED_TESTS_OVERVIEW,
                  },
                  {
                    id: "teacher-live-test-monitoring",
                    dataCy: "teacher-live-test-monitoring",
                    title: t("teacherSideMenu.testing.monitoring.title", "Monitor live test"),
                    redirectPath:
                      TEACHER_SCHEDULED_TEST_PAGE_ROUTES.SCHEDULED_TEST_LIVE_MONITOR_OVERVIEW,
                  },
                  {
                    id: "teacher-test-report",
                    dataCy: "teacher-test-report",
                    title: t("teacherSideMenu.testing.report.title", "Test results"),
                    redirectPath:
                      TEACHER_SCHEDULED_TEST_PAGE_ROUTES.SCHEDULED_TEST_RESULTS_OVERVIEW,
                  },
                ],
              },
            ]
          : []),
      ],
      [
        currentLanguage,
        productDetails,
        isAdaptiveModuleAvailable,
        isWritingModuleAvailable,
        isTeacherABusinessUser,
        isTeacherAndCanInviteStudents,
        showTeacherNavigationDashboardOverview,
        isInsightsExpanded,
        isTestingExpanded,
      ],
    );

    const onMenuButtonClick = (event: React.MouseEvent) => {
      event.stopPropagation();
      toggleSideMenu();
    };

    const onCloseSideMenu = () => {
      if (isModalOpen(modalIds.modalSideMenu)) {
        toggleModal(modalIds.modalSideMenu);
      }
    };

    const productSpecificRouteMatch = matchPath<InsightPagesPathParams>(location.pathname, {
      path: [
        TEACHER_INSIGHT_PAGE_ROUTES.BASE_PATH,
        TEACHER_HOME_PAGE_ROUTES.DASHBOARD,
        TEACHER_HOME_PAGE_ROUTES.TEACHER_INSTRUCTIONS,
      ],
    });
    const selectedProductId =
      productSpecificRouteMatch && +productSpecificRouteMatch.params.productId;

    return (
      <aside>
        <StyledNavigationContainer
          gridTemplateColumns="1fr"
          gridTemplateRows={!isBusinessDomain() ? "auto 1fr auto" : "1fr auto"}
        >
          {!isBusinessDomain() && (
            <StyledLogoContainer>
              <Link aria-label={t("header.button.goToMainPage")} to={GENERIC_ROUTES.HOME_PAGE}>
                <StyledLogo dataCy="sidebar-app-logo" iconName={getCustomLogoByDomain()} />
              </Link>
            </StyledLogoContainer>
          )}
          <NavigationMenu className={className} dataCy={dataCy} menuItems={menuItems} />
          <StyledNavigationBottomContainer>
            {selectedProductId && (
              <StyledProductSelectorItem>
                <StyledIcon iconName="far fa-dot-circle" iconSize="lg" />
                <InsightProductSelector selectedProductId={selectedProductId} />
              </StyledProductSelectorItem>
            )}
            {showImmersiveReader && <StyledAccessibilityMenuButton />}
            <StyledButton
              dataCy="teacher-header-go-to-student-env-button"
              variant="default"
              onClick={() => navigateToStudentEnvironment()}
            >
              <StyledIcon iconName="far fa-user-graduate" iconSize="lg" />
              {t("header.toStudentEnvButton.title", "To practice environment")}
            </StyledButton>
            {shouldShowSideNav && (
              <StyledProfileButton
                alignItems="center"
                data-cy="open-side-menu-button"
                gap={1}
                onClick={onMenuButtonClick}
              >
                <img alt="icon" src={profileIcon} />
                <CssFlex flexDirection="column">
                  <ParagraphLarge data-cy="account-header-name">{`${userDetails?.firstName} ${userDetails?.lastName}`}</ParagraphLarge>
                  <ParagraphMedium>{userDetails?.schoolNumber}</ParagraphMedium>
                </CssFlex>
              </StyledProfileButton>
            )}
          </StyledNavigationBottomContainer>
        </StyledNavigationContainer>
        {/* -- Modals (must be out of fixed nav container to avoid z-index issues) -- */}
        {shouldShowSideNav && (
          <>
            <SideMenuModal
              userDetails={userDetails}
              onClose={onCloseSideMenu}
              onLogout={onLogout}
              onSubmit={updateUserDetails}
            />
            <AccessibilityMenuModal onClose={onCloseSideMenu} />
          </>
        )}
      </aside>
    );
  },
);
